import type { BattleResult, CardId } from '../../engine/types';

export const PLAYER_MODEL_VERSION = 'player-style-1';
export const PLAYER_MODEL_HISTORY_LIMIT = 50;

export interface PlayerStyleVector {
  readonly aggression: number;
  readonly cycle: number;
  readonly beatdown: number;
  readonly airPressure: number;
  readonly swarm: number;
  readonly spellControl: number;
  readonly defense: number;
  readonly lanePreference: number;
}

export interface PlayerMatchFeatures {
  readonly matchId: string;
  readonly style: PlayerStyleVector;
  readonly plays: number;
  readonly averageCostMilli: number;
  readonly playerDeck: readonly CardId[];
  readonly botDeck: readonly CardId[];
  readonly result: BattleResult;
}

export interface StrategyOutcome {
  readonly wins: number;
  readonly losses: number;
  readonly draws: number;
}

export interface PlayerLearningModel {
  readonly schemaVersion: 1;
  readonly modelVersion: typeof PLAYER_MODEL_VERSION;
  readonly contentVersion: string;
  readonly revision: number;
  readonly matchCount: number;
  readonly confidence: number;
  readonly style: PlayerStyleVector;
  readonly processedMatchIds: readonly string[];
  readonly recentMatches: readonly PlayerMatchFeatures[];
  readonly strategyOutcomes: Readonly<Record<string, StrategyOutcome>>;
}

export const EMPTY_PLAYER_STYLE: PlayerStyleVector = Object.freeze({
  aggression: 0,
  cycle: 0,
  beatdown: 0,
  airPressure: 0,
  swarm: 0,
  spellControl: 0,
  defense: 0,
  lanePreference: 0,
});

export function createEmptyPlayerLearningModel(contentVersion: string): PlayerLearningModel {
  return {
    schemaVersion: 1,
    modelVersion: PLAYER_MODEL_VERSION,
    contentVersion,
    revision: 0,
    matchCount: 0,
    confidence: 0,
    style: EMPTY_PLAYER_STYLE,
    processedMatchIds: [],
    recentMatches: [],
    strategyOutcomes: {},
  };
}

export function dominantPlayerStyle(style: PlayerStyleVector): Exclude<keyof PlayerStyleVector, 'lanePreference'> {
  const entries = (Object.entries(style) as Array<[keyof PlayerStyleVector, number]>)
    .filter(([key]) => key !== 'lanePreference')
    .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]));
  return entries[0]?.[0] as Exclude<keyof PlayerStyleVector, 'lanePreference'> ?? 'aggression';
}

export function hashPlayerModel(model: PlayerLearningModel): string {
  const source = JSON.stringify({
    modelVersion: model.modelVersion,
    contentVersion: model.contentVersion,
    revision: model.revision,
    matchCount: model.matchCount,
    confidence: model.confidence,
    style: model.style,
    strategyOutcomes: model.strategyOutcomes,
  });
  let value = 0x811c9dc5;
  for (let index = 0; index < source.length; index += 1) {
    value ^= source.charCodeAt(index);
    value = Math.imul(value, 0x01000193);
  }
  return (value >>> 0).toString(16).padStart(8, '0');
}
